import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellIcon, BellOffIcon, BellOnIcon, PlusIcon } from '../components/Brand/Icon';
import { useNotifications } from './NotificationsProvider';
import { notificationRequest } from './api';
import { getUser, getLocalStorage } from '../utils/func';
import config from '../utils/config';
import url from '../utils/url';

export default function ProfileBell({ userId, own, onError }) {
  const { enabled, hasUnseen } = useNotifications();
  const [active, setActive] = useState(false), [loading, setLoading] = useState(false), [ready, setReady] = useState(false);
  const pending = useRef(false), version = useRef(0);
  const advanceVersion = useCallback(() => ++version.current, []);
  const { t } = useTranslation();
  const navigate = useNavigate(), viewerId = getUser()?.id, token = getLocalStorage(config.token), signedIn = Boolean(viewerId && token);
  useEffect(() => {
    const current = advanceVersion(), controller = new AbortController();
    pending.current = false; setActive(false); setLoading(false); setReady(false);
    if (enabled && !own && signedIn && userId) {
      notificationRequest(`/authors/${userId}`, { signal: controller.signal, token }).then(result => {
        if (version.current === current) { setActive(result.active); setReady(true); }
      }).catch(() => { /* the first tap can retry this read */ });
    }
    return () => { advanceVersion(); controller.abort(); };
  }, [enabled, own, userId, signedIn, viewerId, token, advanceVersion]);
  if (!enabled) return null;
  const click = async () => {
    if (own) { navigate(url('/notifications')); return; }
    if (!signedIn) { navigate(`${url('/login')}?from=${encodeURIComponent(window.location.pathname)}`); return; }
    if (pending.current) return;
    pending.current = true; setLoading(true);
    const current = advanceVersion();
    try {
      const before = ready ? active : (await notificationRequest(`/authors/${userId}`, { token })).active;
      if (version.current !== current) return;
      const result = await notificationRequest(`/authors/${userId}`, { method: 'PUT', body: { active: !before }, token });
      if (version.current === current) { setActive(result.active); setReady(true); }
    } catch { if (version.current === current) onError?.(); }
    finally { if (version.current === current) { pending.current = false; setLoading(false); } }
  };
  const Drawing = own ? BellIcon : active ? BellOnIcon : BellOffIcon;
  return <button type="button" className="profile-bell" disabled={loading} aria-busy={loading}
    aria-label={t(`notifications:${own ? 'title' : active ? 'unwatch' : 'watch'}`)}
    aria-pressed={own ? undefined : active} onClick={click}>
    <Drawing />{own && hasUnseen && <span className="profile-bell-new" role="img" aria-label={t('notifications:newActivity')}><PlusIcon /></span>}
  </button>;
}
